import React, { useEffect, useState } from 'react';
import { Container, TextField, Typography, FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import { changePassword, getAccount, getCampaign, updateProfile, updateNotifications, updateCampaignCode } from '../../api';
import { useAlerts } from '../../alerts';
import { Button, LineItem } from '@lexcelon/react-util';

const MyAccount = () => {
  const { clearErrors, setError } = useAlerts();
  const [user, setUser] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [points, setPoints] = useState(null);
  const [inChangePassword, setInChangePassword] = useState(false);
  const [inUpdateProfile, setInUpdateProfile] = useState(false);
  const [inEditNotifications, setInEditNotifications] = useState(false);
  const [inChangeCampaign, setInChangeCampaign] = useState(false);

  useEffect(() => {
    retrieveUser();

    retrieveCampaign();

    return () => {
      clearErrors();
    }
  }, [setError, clearErrors]);

  const retrieveUser = () => {
    getAccount().then(user => {
      setUser(user);
    }).catch(error => {
      setError(error ?? 'Unable to retrieve account information');
    });
  }

  const retrieveCampaign = () => {
    getCampaign().then(userCampaign => {
      setCampaign(userCampaign?.getCampaign());
      setPoints(userCampaign?.getPointsAwarded());
    }).catch(error => {
      setError(error ?? 'Unable to retrieve campaign');
    });
  }

  if (inChangePassword) return (
    <ChangePassword
      onClose={() => setInChangePassword(false)}
    />
  );
  if (inUpdateProfile) return (
    <EditProfile
      user={user}
      onClose={() => {
        setInUpdateProfile(false);
        retrieveUser();
      }}
    />
  );
  if (inEditNotifications) return (
    <EditNotifications
      user={user}
      onClose={() => {
        setInEditNotifications(false);
        retrieveUser();
      }}
    />
  );
  if (inChangeCampaign) return (
    <ChangeCampaign
      onClose={() => {
        setInChangeCampaign(false);
        retrieveCampaign();
      }}
    />
  );
  return (
    <Container>
      <Typography variant='h1' style={{ marginTop: '1em', marginBottom: '1em' }}>My Account</Typography>

      {/* Personal Information */}
      <Typography variant='h2'>Personal Information</Typography>
      <LineItem
        value={user?.getFirstName()}
        description='First Name'
      />
      <LineItem
        value={user?.getLastName()}
        description='Last Name'
      />
      <LineItem
        value={user?.getEmail()}
        description='Email'
      />
      <LineItem
        value={user?.getPhoneNumber()}
        description='Phone Number'
      />
      <LineItem
        value={user?.getScreenName()}
        description='Screen Name'
      />

      <div style={{ display: 'flex', flexDirection: 'column', width: '200px' }}>
        <Button style={{ width: '100%', marginBottom: '10px' }} onClick={() => setInUpdateProfile(true)}>Edit Profile</Button>
        <Button style={{ width: '100%' }} onClick={() => setInChangePassword(true)}>Change Password</Button>
      </div>

      {/* Notifications */}
      <Typography variant='h2' gutterBottom style={{ marginTop: '1em' }}>Notifications</Typography>
      <LineItem
        value={user?.getNotifyEmail() ? 'Yes' : 'No'}
        description='Email Notifications'
      />
      <LineItem
        value={user?.getNotifyPhone() ? 'Yes' : 'No'}
        description='Phone Notifications'
      />
      <LineItem
        value={user?.getNotifyPush() ? 'Yes' : 'No'}
        description='Push Notifications'
      />
      <Button style={{ width: '200px' }} onClick={() => setInEditNotifications(true)}>Edit Notifications</Button>

      {/* Identity Information */}
      <Typography variant='h2' gutterBottom style={{ marginTop: '1em' }}>Identity Information</Typography>
      <LineItem
        value={user?.getCaregivingStatus()?.split(';;;')?.join(', ') ?? 'Not Answered'}
        description='Caregiving Status'
      />

      <LineItem
        value={user?.getEthnicity() ?? 'Not Answered'}
        description='Ethnicity'
      />

      <LineItem
        value={user?.getRace()?.split(';;;')?.join(', ') ?? 'Not Answered'}
        description='Race'
      />

      <LineItem
        value={user?.getGender()?.split(';;;')?.join(', ') ?? 'Not Answered'}
        description='Gender'
      />

      <LineItem
        value={user?.getSexualOrientation() ?? 'Not Answered'}
        description='Sexual Orientation'
      />

      <LineItem
        value={user?.getGeneration() ?? 'Not Answered'}
        description='Generation'
      />

      <LineItem
        value={user?.getLocation() ?? 'Not Answered'}
        description='Location'
      />

      <LineItem
        value={user?.getDisability()?.split(';;;')?.join(', ') ?? 'Not Answered'}
        description='Disability'
      />

      <LineItem
        value={user?.getLanguageAccent()?.split(';;;')?.join(', ') ?? 'Not Answered'}
        description='Language Accent'
      />

      <LineItem
        value={user?.getRelationshipStatus() ?? 'Not Answered'}
        description='Relationship Status'
      />

      <LineItem
        value={user?.getMilitaryAffiliation()?.split(';;;')?.join(', ') ?? 'Not Answered'}
        description='Military Affiliation'
      />

      <LineItem
        value={user?.getReligiousAffiliation() ?? 'Not Answered'}
        description='Religious Affiliation'
      />

      <LineItem
        value={user?.getSupervisoryStatus() ?? 'Not Answered'}
        description='Supervisory Status'
      />

      <LineItem
        value={user?.getTenure() ?? 'Not Answered'}
        description='Tenure'
      />

      <LineItem
        value={user?.getUnionAffiliation() ?? 'Not Answered'}
        description='Union Affiliation'
      />

      <LineItem
        value={user?.getWorkLocation() ?? 'Not Answered'}
        description='Work Location'
      />

      <LineItem
        value={user?.getOpenIdentity() ?? 'Not Answered'}
        description='Open Identity'
      />
      <Button style={{ width: '200px' }} onClick={() => setInChangePassword(true)}>Edit Identity Information</Button>

      {/* Campaign Info */}
      <Typography variant='h2' gutterBottom style={{ marginTop: '1em' }}>My Campaign</Typography>
      <LineItem
        value={campaign?.getOrganizationName()}
        description='Active Campaign'
      />
      <LineItem
        value={points}
        description='Points Awarded'
      />
      <Button style={{ width: '200px' }} danger onClick={() => setInChangeCampaign(true)}>Change Campaign</Button>
    </Container>
  );
}
export default MyAccount;

const EditProfile = ({ onClose, user }) => {
  const { setError, setSuccess } = useAlerts();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [screenName, setScreenName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setFirstName(user?.getFirstName() ?? '');
    setLastName(user?.getLastName() ?? '');
    setPhoneNumber(user?.getPhoneNumber() ?? '');
    setScreenName(user?.getScreenName() ?? '');
  }, [user])

  const onSubmit = (e) => {
    e.preventDefault();

    setIsLoading(true);
    updateProfile({ firstName, lastName, phoneNumber: phoneNumber !== '' ? phoneNumber : undefined, screenName }).then(() => {
      setSuccess('Profile updated successfully');
      onClose();
    }).catch(error => {
      setError(error ?? 'Unable to update profile');
    }).finally(() => {
      setIsLoading(false);
    });
  }

  return (
    <Container>
      <Typography variant='h1' style={{ marginTop: '1em', marginBottom: '1em' }}>Edit Profile</Typography>
      <form onSubmit={onSubmit}>

        {/* First Name */}
        <TextField
          required
          label='First Name'
          onChange={(e) => setFirstName(e.target.value)}
          value={firstName}
          disabled={isLoading}
          variant='filled'
          style={{
            width: '100%',
            marginBottom: '0.5em'
          }}
          inputProps={{
            maxLength: 10
          }}
        />

        {/* Last Name */}
        <TextField
          required
          label='Last Name'
          onChange={(e) => setLastName(e.target.value)}
          value={lastName}
          disabled={isLoading}
          variant='filled'
          style={{
            width: '100%',
            marginBottom: '0.5em'
          }}
          inputProps={{
            maxLength: 10
          }}
        />

        {/* Phone Number */}

        {/* Screen Name */}
        <TextField
          required
          label='Screen Name'
          onChange={(e) => setScreenName(e.target.value)}
          value={screenName}
          disabled={isLoading}
          variant='filled'
          style={{
            width: '100%',
            marginBottom: '0.5em'
          }}
          inputProps={{
            maxLength: 10
          }}
        />

        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', marginTop: '10px' }}>
          <Button type='submit' isLoading={isLoading} style={{ width: '200px' }}>Update Profile</Button>
          <Button secondary onClick={onClose} disabled={isLoading} style={{ marginTop: '10px', width: '200px' }}>Cancel</Button>
        </div>
      </form>
    </Container>
  );
}

const ChangePassword = ({ onClose }) => {
  const { setError, setSuccess } = useAlerts();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    changePassword(password).then(() => {
      setIsLoading(false);
      setSuccess('Successfully changed password');
      onClose();
    }).catch(error => {
      setIsLoading(false);
      setError(error ?? 'Unable to change password');
    });
  }

  return (
    <Container>
      <Typography variant='h1' style={{ marginTop: '1em', marginBottom: '1em' }}>Change Password</Typography>
      <form onSubmit={onSubmit}>

        {/* New Password */}
        <TextField
          required
          label='New Password'
          type='password'
          onChange={(e) => setPassword(e.target.value)}
          disabled={isLoading}
          variant='filled'
          style={{
            width: '100%',
            marginBottom: '0.5em'
          }}
        />

        {/* Confirm New Password */}
        <TextField
          required
          label='Confirm New Password'
          type='password'
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={isLoading}
          variant='filled'
          style={{
            width: '100%',
            marginBottom: '0.5em'
          }}
        />

        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', marginTop: '10px' }}>
          <Button type='submit' isLoading={isLoading} style={{ width: '200px' }}>Change Password</Button>
          <Button secondary onClick={onClose} disabled={isLoading} style={{ marginTop: '10px', width: '200px' }}>Cancel</Button>
        </div>
      </form>
    </Container>
  );
}

const EditNotifications = ({ onClose, user }) => {
  const { setError, setSuccess } = useAlerts();
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notifyPhone, setNotifyPhone] = useState(false);
  const [notifyPush, setNotifyPush] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  useEffect(() => {
    setNotifyEmail(user?.getNotifyEmail() ?? false);
    setNotifyPhone(user?.getNotifyPhone() ?? false);
    setNotifyPush(user?.getNotifyPush() ?? false);
  }, [user]);

  const onSubmit = (e) => {
    e.preventDefault();

    if (!agreeToTerms) {
      setError('You must agree to the terms');
      return;
    }

    setIsLoading(true);
    updateNotifications({ notifyEmail, notifyPhone, notifyPush }).then(() => {
      setSuccess('Notifications updated successfully');
      onClose();
    }).catch(error => {
      setError(error ?? 'Unable to update notifications');
    }).finally(() => {
      setIsLoading(false);
    });
  }

  return (
    <Container>
      <Typography variant='h1' style={{ marginTop: '1em', marginBottom: '1em' }}>Edit Notifications</Typography>
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

        <FormGroup style={{ width: '300px' }}>
          <FormControlLabel control={<Checkbox checked={notifyEmail} onChange={e => setNotifyEmail(e.target.checked)} />} label='Receive Email Notifications' />
          <FormControlLabel control={<Checkbox checked={notifyPhone} onChange={e => setNotifyPhone(e.target.checked)} />} label='Receive Text Notifications' />
          <FormControlLabel control={<Checkbox disabled checked={notifyPush} onChange={e => setNotifyPush(e.target.checked)} />} label='Receive Push Notifications' />
        </FormGroup>

        <Typography variant='h2' style={{ marginTop: '1em' }}>Do you agree to be notified of new engagements through the methods selected above?*</Typography>
        <Typography variant='subtitle' style={{ textTransform: 'uppercase', width: '100%', marginBottom: '0.5em', marginTop: '0.5em' }}>We will not use your contact information for any marketing purposes.</Typography>
        <FormGroup row style={{ width: '100%' }}>
          <FormControlLabel control={<Checkbox checked={agreeToTerms} onChange={e => setAgreeToTerms(e.target.checked)} />} label='Yes' />
          <FormControlLabel control={<Checkbox checked={!agreeToTerms} onChange={e => setAgreeToTerms(!e.target.checked)} />} label='No' />
        </FormGroup>

        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', marginTop: '10px' }}>
          <Button type='submit' isLoading={isLoading} style={{ width: '200px' }}>Update Notifications</Button>
          <Button secondary onClick={onClose} disabled={isLoading} style={{ marginTop: '10px', width: '200px' }}>Cancel</Button>
        </div>
      </form>
    </Container>
  );
}

const ChangeCampaign = ({ onClose }) => {
  const { setError, setSuccess } = useAlerts();
  const [campaignCode, setCampaignCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();

    setIsLoading(true);
    updateCampaignCode(campaignCode).then(() => {
      setSuccess('Successfully changed campaign');
      onClose();
    }).catch(error => {
      setError(error ?? 'Unable to change campaign');
    }).finally(() => {
      setIsLoading(false);
    });
  }

  return (
    <Container>
      <Typography variant='h1' style={{ marginTop: '1em', marginBottom: '0.5em', color: 'red' }}>Change Campaign</Typography>
      <Typography variant='body1' style={{ marginBottom: '1em', fontWeight: 'bold', textAlign: 'center', color: 'red' }}>WARNING: Please be sure you want to leave your current campaign.</Typography>
      <form onSubmit={onSubmit}>

        {/* Campaign Code */}
        <TextField
          required
          label='Campaign Code'
          onChange={(e) => setCampaignCode(e.target.value)}
          disabled={isLoading}
          variant='filled'
          style={{
            width: '100%',
            marginBottom: '0.5em'
          }}
        />

        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', marginTop: '10px' }}>
          <Button type='submit' isLoading={isLoading} style={{ width: '200px' }}>Save</Button>
          <Button secondary onClick={onClose} disabled={isLoading} style={{ marginTop: '10px', width: '200px' }}>Cancel</Button>
        </div>
      </form>
    </Container>
  );
}